import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import {
  getSecurityEscalationMatrix,
  type SecurityEscalationMatrixRow,
} from "../../../service/securityService";

const SecurityEscalationMatrix: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<SecurityEscalationMatrixRow[]>([]);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const d = await getSecurityEscalationMatrix();
      setRows(d.matrix || []);
      setNote(d.note || "");
    } catch (e) {
      console.error(e);
      toast.error("Failed to load escalation matrix");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
      <div className="flex justify-between items-center px-4 py-3 border-b border-gray-100">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Emergency escalation matrix</h2>
          <p className="text-sm text-gray-600">
            Severity drives response time targets and default notification paths (integrate with your
            directory and paging provider).
          </p>
        </div>
        <button
          type="button"
          onClick={() => load()}
          className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-full text-sm hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>
      {note && <p className="text-xs text-gray-500 px-4 py-2 bg-gray-50 border-b">{note}</p>}
      {loading ? (
        <div className="flex justify-center py-16">
          <SpinLoader />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-[#E9F5FF]">
              <tr>
                <th className="p-3 font-semibold">Severity</th>
                <th className="p-3 font-semibold">SLA target</th>
                <th className="p-3 font-semibold">Notify</th>
                <th className="p-3 font-semibold">Channels</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => (
                <tr key={r.severity} className="hover:bg-gray-50/80">
                  <td className="p-3 font-medium text-gray-900">{r.severity}</td>
                  <td className="p-3 text-gray-700">
                    {r.slaMinutes >= 1440
                      ? `${Math.round(r.slaMinutes / 1440)} day(s)`
                      : r.slaMinutes >= 60
                        ? `${Math.round(r.slaMinutes / 60)} hour(s)`
                        : `${r.slaMinutes} min`}
                  </td>
                  <td className="p-3 text-gray-700">
                    <ul className="list-disc list-inside">
                      {(r.notify || []).map((n) => (
                        <li key={n}>{n}</li>
                      ))}
                    </ul>
                  </td>
                  <td className="p-3 text-gray-700">{(r.channels || []).join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SecurityEscalationMatrix;
